import React, {useCallback, useMemo, useState} from "react";
import {Box, Card, Checkbox, CircularProgress, FormControlLabel, Tooltip, Typography} from "@mui/material";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Add, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import LazyImage from "../../../../../../components/app-specific/lazy-image";

const CategoryManagementViewCategoryCard = ({
                                                category,
                                                siblingCategories,
                                                leafCategories,
                                                parent,
                                                toggleActive,
                                                upsertCategory,
                                                removeCategory,
                                                removing,
                                                depth,
                                                parentColor,
                                            }) => {
    const [togglingIsActive, setTogglingIsActive] = useState(false);
    const isMounted = useIsMounted();

    const newCategory = useMemo(() => !category, [category]);
    const isRoot = useMemo(() => !parent, [parent]);

    /**
     * Changes the is active status of the category.
     * @type {(function(*): Promise<void>)}
     */
    const changeIsActive = useCallback(async (e) => {
        setTogglingIsActive(true);
        await toggleActive(category?.id, e.target.checked)
        if (!isMounted())
            return;
        setTogglingIsActive(false);
    }, [isMounted, toggleActive, category?.id])

    const parentColorWithDepth = useMemo(() => {
        const transparency = ['FF', 'E0', 'C0', 'A0', '80', '60', '40', '20', '00']
        const index = Math.min(depth, transparency.length - 1);
        if (index === transparency.length - 1) {
            return null;
        }
        return parentColor ? `${parentColor}${transparency[Math.min(depth, transparency.length - 1)]}` : null;
    }, [parentColor, depth])

    if (newCategory) {
        return (
            <Tooltip title={`Add new ${isRoot ? "root" : "sub"} category ${isRoot ? '' : `for ${parent?.title ?? ""}`}`}>
                <Card
                    variant={'outlined'}
                    onClick={() => upsertCategory(parent?.id)}
                    sx={{
                        padding: 1,
                        color: 'text.primary',
                        border: '2px dashed transparent',
                        borderColor: 'divider',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <Add className={'mr-2'} color={'primary'}/>
                    <Typography variant={'body2'} fontWeight={600} color={'primary.main'}>
                        Add a new {isRoot ? "root" : 'sub'} Category
                    </Typography>
                </Card>
            </Tooltip>
        );
    }

    return (
        <>
            <Card
                onClick={e => {
                    e.stopPropagation();
                }}
                data-id={category.id}
                variant={'outlined'}
                sx={{
                    padding: 1,
                    color: 'text.primary',
                    borderColor: 'divider',
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    ...(parentColorWithDepth && {
                        borderColor: `${parentColorWithDepth}`,
                        background: `linear-gradient(to top, ${parentColorWithDepth} -1000%, transparent 100%)`,
                    }),
                }}
            >
                <Box sx={{display: 'flex', width: '100%', gap: 1}}>
                    <LazyImage
                        src={category.coverImage ?? ""}
                        alt={category.title ?? ""}
                        style={{
                            borderRadius: 8,
                            width: 70,
                            height: 70,
                            objectFit: 'contain',
                            objectPosition: 'center',
                        }}
                    />
                    <div className={'d-flex flex-column w-100'}>
                        <div className={'d-flex align-items-center justify-content-between w-100'}>
                            <Typography variant={'body2'} fontWeight={600}>
                                {category.title ?? ""}
                            </Typography>
                            {
                                category.editable && !category.isPredefined &&
                                <>
                                    <div className={'ml-1 d-flex justify-content-end'}>
                                        <IconButton
                                            color={'paper.primary'}
                                            size={'small'}
                                            disabled={removing || togglingIsActive}
                                            onPressed={() => upsertCategory(parent?.id, category)}
                                        >
                                            <EditOutlined className={'icon small'}/>
                                        </IconButton>
                                        {
                                            category.children?.length === 0 &&
                                            <IconButton
                                                color={'paper.error'}
                                                size={'small'}
                                                disabled={removing || togglingIsActive}
                                                onPressed={() => removeCategory(category, leafCategories.filter(c => c.id !== category.id))}
                                            >
                                                {
                                                    removing
                                                        ? <CircularProgress size={20}/>
                                                        : <DeleteOutlined className={'icon small'}/>
                                                }
                                            </IconButton>
                                        }
                                    </div>
                                </>
                            }
                        </div>

                        <Typography variant={'caption'} color={'text.secondary'} fontWeight={500}>
                            {category.children?.length ?? 0} sub-categories
                        </Typography>
                        <div className={'d-flex justify-content-between w-100'}>
                            <Typography
                                sx={{opacity: category.isPredefined ? 1 : 0}}
                                variant={'overline'}
                                color={'text.disabled'}
                                fontWeight={500}
                            >
                                Predefined
                            </Typography>
                            <FormControlLabel
                                label="Active"
                                labelPlacement={'start'}
                                sx={{margin: 0}}
                                control={
                                    <Checkbox
                                        size={'small'}
                                        disabled={removing || togglingIsActive}
                                        sx={{padding: 0, paddingInlineStart: 0.5}}
                                        disableRipple
                                        checked={category.active}
                                        onChange={changeIsActive}
                                    />
                                }
                            />
                        </div>

                    </div>
                </Box>
            </Card>
        </>
    );
}

export default CategoryManagementViewCategoryCard;
